import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Booking from "@/pages/Booking";
import Activate from "@/pages/Activate";
import Reset from "@/pages/Reset";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import Missions from "@/pages/Missions";
import Clients from "@/pages/Clients";
import Demandes from "@/pages/Demandes";
import Invoices from "@/pages/Invoices";
import Expenses from "@/pages/Expenses";
import Subscriptions from "@/pages/Subscriptions";
import FuelAccounts from "@/pages/FuelAccounts";
import Fleet from "@/pages/Fleet";
import Places from "@/pages/Places";
import Dispatch from "@/pages/Dispatch";
import Profitability from "@/pages/Profitability";
import Journal from "@/pages/Journal";
import Messages from "@/pages/Messages";
import Users from "@/pages/Users";
import SettingsPage from "@/pages/SettingsPage";
import { useVocab } from "@/lib/vocab";
import { useRights } from "@/lib/rights";

function Protected({ children, managers }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (managers && user.role === "employee") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

// Page réservée à un métier (lot T0) : ailleurs, retour au tableau de bord.
function OnlyFor({ business, children }) {
  const voc = useVocab();
  return voc.business === business ? children : <Navigate to="/" replace />;
}

// Page réservée à un droit (lot T5-0, lib/rights.js) : la secrétaire du transport n'a pas Utilisateurs ; Réglages :
// partie facturation seulement (lot T5-0b, la page se réduit d'elle-même).
// En conciergerie, tout gestionnaire a ces droits : rien ne change chez M CARWASH.
function NeedRight({ right, children }) {
  const rights = useRights();
  return rights[right] ? children : <Navigate to="/" replace />;
}

// Page d'arrivée (lot T5a, lib/rights.js : dispatchHome) : en transport, le Patron et le dispatcheur arrivent sur le
// Dispatch (le Tableau de bord passe à /dashboard) ; tous les autres, et toute la conciergerie, gardent le Tableau de
// bord sur « / ».
function Home() {
  const rights = useRights();
  return rights.dispatchHome ? <Navigate to="/dispatch" replace /> : <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/book" element={<Booking />} />
              <Route path="/activate" element={<Activate />} />
              <Route path="/reset" element={<Reset />} />
              <Route path="/" element={<Protected><Home /></Protected>} />
              <Route path="/dispatch" element={<Protected managers><OnlyFor business="transport"><NeedRight right="dispatch"><Dispatch /></NeedRight></OnlyFor></Protected>} />
              <Route path="/dashboard" element={<Protected managers><OnlyFor business="transport"><Dashboard /></OnlyFor></Protected>} />
              <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
              <Route path="/missions" element={<Protected><Missions /></Protected>} />
              <Route path="/clients" element={<Protected managers><Clients /></Protected>} />
              <Route path="/subscriptions" element={<Protected managers><OnlyFor business="conciergerie"><Subscriptions /></OnlyFor></Protected>} />
              <Route path="/fuel" element={<Protected managers><OnlyFor business="conciergerie"><FuelAccounts /></OnlyFor></Protected>} />
              <Route path="/vehicules" element={<Protected managers><OnlyFor business="transport"><Fleet /></OnlyFor></Protected>} />
              <Route path="/lieux" element={<Protected managers><OnlyFor business="transport"><Places /></OnlyFor></Protected>} />
              <Route path="/demandes" element={<Protected managers><Demandes /></Protected>} />
              <Route path="/journal" element={<Protected managers><Journal /></Protected>} />
              <Route path="/invoices" element={<Protected managers><Invoices /></Protected>} />
              <Route path="/expenses" element={<Protected managers><Expenses /></Protected>} />
              <Route path="/profitability" element={<Protected managers><Profitability /></Protected>} />
              <Route path="/messages" element={<Protected><Messages /></Protected>} />
              <Route path="/users" element={<Protected managers><NeedRight right="users"><Users /></NeedRight></Protected>} />
              <Route path="/settings" element={<Protected managers><NeedRight right="settingsBilling"><SettingsPage /></NeedRight></Protected>} />
            </Routes>
          </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
